/**
 * ZonePolygonLayer - Displays zone polygons on the map
 *
 * Shows ONLY INACTIVE zones as clickable polygons with rich tooltips.
 * The active zone is NOT shown since its network elements are already displayed.
 * Clicking on an inactive zone switches to that zone.
 */
import { FC, memo, useMemo, useCallback, useState, useEffect } from 'react';
import { Polygon, Tooltip, Marker } from 'react-leaflet';
import L from 'leaflet';
import { puToVolts } from '../../utils/electricalConstants';
import { loadingSeverity } from '../../utils/loadingConstants';
import {
  VOLTAGE_OPERATIONAL_MIN_PU, VOLTAGE_OPERATIONAL_MAX_PU,
  VOLTAGE_WARNING_LOW_PU, VOLTAGE_WARNING_HIGH_PU,
} from '../../utils/voltageConstants';
import { useZoneStore } from '../../store/zoneStore';
import { zoneService } from '../../services/zoneService';
import { switchZone } from '../../services/zoneSwitchService';
import type { NetworkZone, ZoneStatisticsSummary } from '../../types/zone';
import { CONNECTION_KIND_SHORT, type ConnectionKind } from '../../types/network';
import { fmt } from '../../utils/numberFormat';
import { polygonToLatLngs, polygonCentroid } from '../../utils/zonePolygonGeometry';

// ── Color palette for visually distinct zone polygons ────────────────────────

/** " (12 G · 3 RLM)" — non-household connections of the zone, empty when there are none. */
function kindSuffix(kinds: ZoneStatisticsSummary['houseConnectionKinds']): string {
  if (!kinds) return '';
  const parts = (Object.keys(kinds) as ConnectionKind[]).filter(k => (kinds[k] ?? 0) > 0).map(k => `${kinds[k]} ${CONNECTION_KIND_SHORT[k]}`);
  return parts.length ? ` (${parts.join(' · ')})` : '';
}

const ZONE_PALETTE = [
  '#3B82F6', // blue
  '#10B981', // emerald
  '#F59E0B', // amber
  '#8B5CF6', // violet
  '#EF4444', // red
  '#14B8A6', // teal
  '#EC4899', // pink
  '#F97316', // orange
  '#6366F1', // indigo
  '#84CC16', // lime
];

// ── Tooltip sub-components ──────────────────────────────────────────────────

function TooltipLoadingBar({ value, label }: { value: number | null | undefined; label: string }) {
  const hasData = value != null;
  const val = value ?? 0;
  const severity = loadingSeverity(val);
  const color = severity === 'critical' ? '#ef4444' : severity === 'warning' ? '#f97316' : '#22c55e';

  return (
    <div style={{ marginBottom: 3 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 10, marginBottom: 1 }}>
        <span style={{ color: '#9ca3af' }}>{label}</span>
        <span style={{ fontWeight: 700, color: hasData ? (severity !== 'normal' ? color : '#374151') : '#9ca3af' }}>
          {hasData ? `${fmt(val, 1)}%` : '-'}
        </span>
      </div>
      <div style={{ height: 3, borderRadius: 2, background: '#e5e7eb' }}>
        {hasData && (
          <div style={{ height: '100%', borderRadius: 2, background: color, width: `${Math.min(val, 100)}%`, transition: 'width 0.3s' }} />
        )}
      </div>
    </div>
  );
}

function TooltipVoltageRow({ label, minPu, maxPu }: { label: string; minPu: number | null | undefined; maxPu: number | null | undefined }) {
  const hasData = minPu != null && maxPu != null && minPu > 0 && maxPu > 0;

  // Red outside the EN 50160 band, orange inside the warning margin, green otherwise.
  const getColor = (v: number) => {
    if (v < VOLTAGE_OPERATIONAL_MIN_PU || v > VOLTAGE_OPERATIONAL_MAX_PU) return '#ef4444';
    if (v < VOLTAGE_WARNING_LOW_PU || v > VOLTAGE_WARNING_HIGH_PU) return '#f97316';
    return '#22c55e';
  };

  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', fontSize: 10, lineHeight: '16px' }}>
      <span style={{ color: '#9ca3af', fontWeight: 600, width: 18 }}>{label}</span>
      {hasData ? (
        <span>
          <span style={{ color: getColor(minPu), fontWeight: 600 }}>{fmt(puToVolts(minPu), 0)}</span>
          <span style={{ color: '#9ca3af' }}> - </span>
          <span style={{ color: getColor(maxPu), fontWeight: 600 }}>{fmt(puToVolts(maxPu), 0)} V</span>
        </span>
      ) : (
        <span style={{ color: '#9ca3af' }}>-</span>
      )}
    </div>
  );
}

// ── Zone Polygon ────────────────────────────────────────────────────────────

interface ZonePolygonProps {
  zone: NetworkZone;
  fillColor: string;
  stats: ZoneStatisticsSummary | undefined;
  onSelect: (zoneId: string) => void;
}

// memo: 147 polygons with rich tooltips - without it every parent render re-rendered them all
const ZonePolygon: FC<ZonePolygonProps> = memo(({ zone, fillColor, stats, onSelect }) => {
  const positions = useMemo(() => {
    if (!zone.polygon) return null;
    return polygonToLatLngs(zone.polygon);
  }, [zone.polygon]);

  const handleClick = useCallback(() => {
    onSelect(zone.id);
  }, [zone.id, onSelect]);

  const centroid = useMemo(() => {
    if (zone.centerLat != null && zone.centerLng != null) {
      return [zone.centerLat, zone.centerLng] as [number, number];
    }
    if (!positions) return null;
    return polygonCentroid(positions);
  }, [zone.centerLat, zone.centerLng, positions]);

  const labelIcon = useMemo(() => {
    return L.divIcon({
      className: 'zone-label-icon',
      html: `<div style="
        color: ${fillColor};
        font-size: 14px;
        font-weight: 800;
        white-space: nowrap;
        text-shadow: -1px -1px 0 #fff, 1px -1px 0 #fff, -1px 1px 0 #fff, 1px 1px 0 #fff, 0 0 6px rgba(255,255,255,0.8);
        transform: translate(-50%, -50%);
        pointer-events: none;
        letter-spacing: 0.3px;
      ">${zone.name}</div>`,
      iconSize: [0, 0],
      iconAnchor: [0, 0],
    });
  }, [fillColor, zone.name]);

  if (!positions || positions.length === 0) {
    return null;
  }

  const pathOptions = {
    color: fillColor,
    fillColor: fillColor,
    fillOpacity: 0.20,
    weight: 2,
  };

  const hasStats = stats != null;
  const hasTrafos = hasStats && stats.trafos?.length > 0;
  const hasVoltage = hasStats && (stats.currentMinVoltageL1Pu != null || stats.currentMinVoltagePu != null);
  const hasCable = hasStats && stats.currentMaxCableLoadingPercent != null;

  return (
    <>
      <Polygon
        positions={positions}
        pathOptions={pathOptions}
        eventHandlers={{ click: handleClick }}
      >
        <Tooltip sticky direction="top" offset={[0, -8]} className="zone-tooltip">
          <div style={{ minWidth: 220, maxWidth: 280, padding: 0 }}>
            {/* Header */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 6, paddingBottom: 5, borderBottom: '2px solid ' + fillColor }}>
              <div style={{ width: 8, height: 8, borderRadius: '50%', background: fillColor, flexShrink: 0 }} />
              <span style={{ fontSize: 13, fontWeight: 700, color: '#111827' }}>{zone.name}</span>
            </div>

            {/* Trafos */}
            {hasTrafos && (
              <div style={{ marginBottom: 6 }}>
                <div style={{ fontSize: 9, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5, color: '#6b7280', marginBottom: 3 }}>
                  Trafostationen
                </div>
                {stats.trafos.map(t => (
                  <TooltipLoadingBar
                    key={t.id}
                    label={t.name + (t.capacityKva > 0 ? ` (${t.capacityKva} kVA)` : '')}
                    value={t.loadingPercent}
                  />
                ))}
              </div>
            )}

            {/* Cable Loading */}
            {(hasCable || hasStats) && (
              <div style={{ marginBottom: 6 }}>
                <div style={{ fontSize: 9, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5, color: '#6b7280', marginBottom: 3 }}>
                  Kabel
                </div>
                <TooltipLoadingBar
                  label="Max. Auslastung"
                  value={stats?.currentMaxCableLoadingPercent}
                />
              </div>
            )}

            {/* Voltage Band L1-L3 */}
            {(hasVoltage || hasStats) && (
              <div style={{ marginBottom: 6 }}>
                <div style={{ fontSize: 9, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5, color: '#6b7280', marginBottom: 2 }}>
                  Spannung L-N
                </div>
                {stats?.currentMinVoltageL1Pu != null ? (
                  <>
                    <TooltipVoltageRow label="L1" minPu={stats.currentMinVoltageL1Pu} maxPu={stats.currentMaxVoltageL1Pu} />
                    <TooltipVoltageRow label="L2" minPu={stats.currentMinVoltageL2Pu} maxPu={stats.currentMaxVoltageL2Pu} />
                    <TooltipVoltageRow label="L3" minPu={stats.currentMinVoltageL3Pu} maxPu={stats.currentMaxVoltageL3Pu} />
                  </>
                ) : (
                  <TooltipVoltageRow label="U" minPu={stats?.currentMinVoltagePu} maxPu={stats?.currentMaxVoltagePu} />
                )}
              </div>
            )}

            {/* Element counts - compact row */}
            {hasStats && (
              <div style={{ display: 'flex', gap: 8, fontSize: 10, color: '#6b7280', marginBottom: 6, flexWrap: 'wrap' }}>
                <span>{stats.houseConnectionCount} Hausanschl.{kindSuffix(stats.houseConnectionKinds)}</span>
                <span>{stats.cableCount} Kabel</span>
                <span>{stats.smgwActiveCount} SMGWs{stats.houseConnectionCount > 0 && ` (${Math.round(stats.smgwActiveCount / stats.houseConnectionCount * 100)}%)`}</span>
              </div>
            )}

            {/* Footer */}
            <div style={{ paddingTop: 4, borderTop: '1px solid #e5e7eb', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <span style={{ fontSize: 10, color: '#6b7280' }}>Klicken zum Wechseln</span>
              <span style={{ fontSize: 12, color: fillColor, fontWeight: 600 }}>&rarr;</span>
            </div>
          </div>
        </Tooltip>
      </Polygon>
      {centroid && (
        <Marker
          position={centroid}
          icon={labelIcon}
          interactive={false}
        />
      )}
    </>
  );
});
ZonePolygon.displayName = 'ZonePolygon';

// ── Main Component ──────────────────────────────────────────────────────────

export const ZonePolygonLayer: FC = memo(() => {
  const zones = useZoneStore(state => state.zones);
  const activeZoneId = useZoneStore(state => state.activeZoneId);
  const [statistics, setStatistics] = useState<ZoneStatisticsSummary[]>([]);

  // Fetch zone statistics once for tooltips (no polling — LandingPage handles periodic refresh)
  useEffect(() => {
    let cancelled = false;

    const fetchStats = async () => {
      try {
        const stats = await zoneService.getZoneStatistics();
        if (!cancelled) setStatistics(stats);
      } catch (err) {
        console.error('[ZonePolygonLayer] Failed to load statistics:', err);
      }
    };

    fetchStats();
    return () => { cancelled = true; };
  }, []);

  const statsMap = useMemo(() => {
    const map = new Map<string, ZoneStatisticsSummary>();
    for (const s of statistics) {
      map.set(s.zoneId, s);
    }
    return map;
  }, [statistics]);

  // Full switch flow (SSE reconnect, cleared stores, polling restart) — the former
  // selectZone + loadElements shortcut left the old zone's SSE stream connected.
  const handleZoneSelect = useCallback((zoneId: string) => {
    void switchZone(zoneId);
  }, []);

  // Stable color assignment: based on position in global zones array (doesn't shift when active zone changes)
  const zoneColorMap = useMemo(() => {
    const map = new Map<string, string>();
    zones.forEach((z, i) => {
      map.set(z.id, ZONE_PALETTE[i % ZONE_PALETTE.length]);
    });
    return map;
  }, [zones]);

  const inactiveZonesWithPolygons = useMemo(() => {
    return zones.filter(z => z.polygon && z.id !== activeZoneId && z.isActive);
  }, [zones, activeZoneId]);

  if (inactiveZonesWithPolygons.length === 0) {
    return null;
  }

  return (
    <>
      {inactiveZonesWithPolygons.map(zone => (
        <ZonePolygon
          key={zone.id}
          zone={zone}
          fillColor={zoneColorMap.get(zone.id) ?? ZONE_PALETTE[0]}
          stats={statsMap.get(zone.id)}
          onSelect={handleZoneSelect}
        />
      ))}
    </>
  );
});
ZonePolygonLayer.displayName = 'ZonePolygonLayer';

export default ZonePolygonLayer;
